import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Megaphone, Plus, Search, Pin, Edit3, Trash2, Eye, EyeOff, Clock, Tag,
} from 'lucide-react';
import api from '../api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function NewsList() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/news');
      setPosts(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete “${title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success('Post deleted');
      setPosts((ps) => ps.filter((p) => p.id !== id));
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (q && !(p.title.toLowerCase().includes(q.toLowerCase()) || (p.excerpt || '').toLowerCase().includes(q.toLowerCase()))) return false;
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      return true;
    });
  }, [posts, q, filterStatus]);

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[11px]">
            <Megaphone className="h-3 w-3" /> News & Announcements
          </div>
          <h1 className="font-serif-display text-[36px] lg:text-[42px] font-bold mt-2">All posts</h1>
          <p className="text-white/55 text-[14px] mt-1">Share community news, updates, and announcements.</p>
        </div>
        <Button onClick={() => navigate('/admin/news/new')} className="h-10 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-5 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]">
          <Plus className="h-4 w-4 mr-1.5" /> New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts…"
            className="h-11 pl-10 rounded-xl bg-[#16131a] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
          />
        </div>
        <div className="flex gap-1 bg-[#16131a] p-1 rounded-full border border-white/[0.06]">
          {[
            { value: 'all', label: 'All' },
            { value: 'published', label: 'Published' },
            { value: 'draft', label: 'Drafts' },
          ].map((o) => (
            <button
              key={o.value} onClick={() => setFilterStatus(o.value)}
              className={`px-3.5 h-9 rounded-full text-[12px] font-semibold transition-colors ${
                filterStatus === o.value ? 'bg-white text-[#0f0d12]' : 'text-white/55 hover:text-white'
              }`}
            >{o.label}</button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] overflow-hidden">
        {loading && <div className="p-10 text-center text-white/50 text-[14px]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-14 text-center">
            <div className="h-14 w-14 rounded-2xl bg-[#E5614C]/15 text-[#E5614C] flex items-center justify-center mx-auto">
              <Megaphone className="h-6 w-6" />
            </div>
            <div className="font-serif-display text-[22px] font-bold mt-4">No posts yet</div>
            <p className="text-white/55 text-[14px] mt-1 max-w-sm mx-auto">Share your first announcement, news story, or community update.</p>
            <Button onClick={() => navigate('/admin/news/new')} className="mt-5 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-10 px-5">
              <Plus className="h-4 w-4 mr-1.5" /> Create post
            </Button>
          </div>
        )}
        {filtered.map((p, i) => (
          <div
            key={p.id}
            className={`group flex items-center gap-5 px-5 py-4 hover:bg-white/[0.04] transition-colors ${i < filtered.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
          >
            <Link to={`/admin/news/${p.id}`} className="flex items-center gap-4 flex-1 min-w-0">
              <div className="h-14 w-14 rounded-xl overflow-hidden bg-[#0f0d12] border border-white/[0.06] shrink-0">
                {p.featured_image ? (
                  <img src={p.featured_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${p.featured_image}` : p.featured_image} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-white/30">
                    <Megaphone className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-serif-display font-bold text-[16px] truncate">{p.title}</div>
                  {p.is_pinned && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-[#FCD968]/20 text-[#FCD968]">
                      <Pin className="h-2.5 w-2.5" fill="currentColor" /> Pinned
                    </span>
                  )}
                  {p.status === 'draft' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/55">
                      <EyeOff className="h-2.5 w-2.5" /> Draft
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-[#5DCAB5]/15 text-[#5DCAB5]">
                      <Eye className="h-2.5 w-2.5" /> Published
                    </span>
                  )}
                  {(p.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-white/8 text-white/65">
                      <Tag className="h-2.5 w-2.5" /> {t}
                    </span>
                  ))}
                </div>
                {p.excerpt && <div className="text-[12px] text-white/55 mt-1 line-clamp-1">{p.excerpt}</div>}
                <div className="text-[11px] text-white/45 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDate(p.published_at || p.created_at)}</span>
                  {p.author_name && <span>by {p.author_name}</span>}
                </div>
              </div>
            </Link>
            <div className="flex items-center gap-1 shrink-0">
              <Link to={`/admin/news/${p.id}`} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/65 hover:text-white transition-colors" title="Edit">
                <Edit3 className="h-4 w-4" />
              </Link>
              <button onClick={() => handleDelete(p.id, p.title)} className="h-9 w-9 rounded-lg hover:bg-red-500/15 flex items-center justify-center text-white/55 hover:text-red-400 transition-colors" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
